import { useEffect, useState } from "react"
import { GetOrderDetailsDto, LineItem } from "../models/api/ordersApiTypes"
import { Link, useParams } from "react-router-dom"
import { getOrderDetailsApi } from "../api/ordersApi"
import { toast } from "react-toastify"
import { createColumnHelper } from "@tanstack/react-table"
import OrderResultPageHeader from "../components/order/OrderResultPageHeader"
import CustomTable from "../components/common/CustomTable"
import OrderSkeleton from "../components/order/OrderSkeleton"

const statusBadgeStyle: Record<string, { background: string; color: string; dot: string }> = {
  "In attesa": { background: "#f5e6c8", color: "#8a6200", dot: "#d4900a" },
  Pagato: { background: "#d4edda", color: "#1a6630", dot: "#28a745" },
  Fallito: { background: "#f8d7da", color: "#721c24", dot: "#dc3545" },
  Scaduto: { background: "#e2e3e5", color: "#383d41", dot: "#6c757d" },
  Rimborsato: { background: "#cce5ff", color: "#004085", dot: "#007bff" },
}

export default function OrderDetailsPage() {
  const [orderResult, setOrderResult] = useState<GetOrderDetailsDto>()
  const { orderId } = useParams()
  const priceMultiplier = 100
  const isDiscountPresent = orderResult?.payment.discount !== undefined ? orderResult.payment.discount > 0 : false
  const badgeStyle = orderResult
    ? (statusBadgeStyle[orderResult.payment.status] ?? { background: "#e2e3e5", color: "#383d41", dot: "#6c757d" })
    : null

  useEffect(() => {
    getOrderDetailsApi(orderId)
      .then((result: GetOrderDetailsDto) => {
        setOrderResult(result)
      })
      .catch(() => toast.warning("Qualcosa è andato storto!"))
  }, [orderId])

  function formatDate(dateString: string): string {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  function formatHour(dateString: string): string {
    const date = new Date(dateString)
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")
    return `${hours}:${minutes}`
  }

  function formatPrice(cents: number): string {
    return (
      (cents / priceMultiplier).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
    )
  }

  const paymentAmountLabel: Record<string, string> = {
    "In attesa": "Importo da pagare",
    Pagato: "Importo pagato",
    Fallito: "Pagamento fallito",
    Scaduto: "Pagamento scaduto",
    Rimborsato: "Importo rimborsato",
  }

  const columnHelper = createColumnHelper<LineItem>()
  const columns = [
    columnHelper.accessor("name", {
      header: () => "NOME PRODOTTO",
      cell: (name) => name.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor("description", {
      header: () => "DESCRIZIONE",
      cell: (description) => description.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor("quantity", {
      header: () => "QUANTITA'",
      cell: (quantity) => quantity.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor("price", {
      header: () => "PREZZO",
      cell: (price) => <>{price.getValue() / priceMultiplier}€</>,
      enableSorting: false,
    }),
  ]

  return (
    <div
      style={{ backgroundColor: "#FFF", maxWidth: "850px" }}
      className="mt-0 mb-0 mt-md-4 mb-md-4 rounded-4 mx-auto p-4"
    >
      <div className="text-end">
        <Link to="/">
          <img src={`${process.env.PUBLIC_URL}/img/icons/close.svg`} alt="Close" />
        </Link>
      </div>

      <OrderResultPageHeader title={"Ordine"} subtitle={`${orderId}`} />

      {orderResult && badgeStyle ? (
        <div style={{ maxWidth: "576px", textAlign: "justify" }} className="mx-auto mt-0 mb-0 mt-sm-4 mb-sm-4">
          <h4 className="mt-4">Informazioni sul tuo ordine</h4>
          <div className="row mb-1 mt-3">
            <div className="col-9 ">
              <div className="col-12 ">
                Ordine di <b>{orderResult.customerName}</b>
              </div>
              <div className="col-12 mb-4">
                {orderResult.customerEmail} - {formatDate(orderResult.created)} alle {formatHour(orderResult.created)}
              </div>
            </div>
            <div className="col-3 d-flex justify-content-end align-items-start">
              <span
                style={{
                  backgroundColor: badgeStyle.background,
                  color: badgeStyle.color,
                  borderRadius: "50px",
                  padding: "4px 10px",
                  fontSize: "13px",
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    backgroundColor: badgeStyle.dot,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                {orderResult.payment.status}
              </span>
            </div>
          </div>
          <CustomTable
            data={orderResult.items}
            columns={columns}
            columnsVisibility={{}}
            pageStartIndex={0}
            pageMaxSize={5}
          />

          <div className="row g-3 mt-3 justify-content-center" style={{ textAlign: "center" }}>
            <div className="col-7 col-md-4">
              <div style={{ backgroundColor: "#f8f9fa", borderRadius: "12px", padding: "16px", height: "100%" }}>
                <div style={{ fontSize: "12px", color: "#888888", marginBottom: "8px" }}>Metodo</div>
                <div style={{ fontWeight: 700, fontSize: "16px" }}>{orderResult.payment.method}</div>
              </div>
            </div>
            <div className="col-7 col-md-4">
              <div style={{ backgroundColor: "#f8f9fa", borderRadius: "12px", padding: "16px", height: "100%" }}>
                <div style={{ fontSize: "12px", color: "#888888", marginBottom: "8px" }}>Totale ordine</div>
                <div style={{ fontWeight: 700, fontSize: "16px" }}>{formatPrice(orderResult.total)}</div>
              </div>
            </div>
            <div className="col-7 col-md-4">
              <div style={{ backgroundColor: "#f8f9fa", borderRadius: "12px", padding: "16px", height: "100%" }}>
                <div style={{ fontSize: "12px", color: "#888888", marginBottom: "8px" }}>
                  {paymentAmountLabel[orderResult.payment.status] ?? "Importo"}
                </div>
                <div style={{ fontWeight: 700, fontSize: "16px" }}>{formatPrice(orderResult.payment.amount)}</div>
              </div>
            </div>
          </div>

          <hr style={{ borderColor: "#e0e0e0", marginTop: "20px" }} />

          {isDiscountPresent && (
            <div
              style={{ backgroundColor: "#f8f9fa", borderRadius: "12px", padding: "16px 20px" }}
              className="d-flex justify-content-between align-items-center mb-3"
            >
              <span style={{ fontSize: "15px" }}>Sconto applicato</span>
              <span style={{ fontWeight: 700, fontSize: "16px", color: "#28a745" }}>
                -{formatPrice(orderResult.payment.discount)}
              </span>
            </div>
          )}

          {orderResult.payment.paymentUrl != null && (
            <a
              href={orderResult.payment.paymentUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-violet w-100 mb-3"
            >
              Vai al pagamento
            </a>
          )}

          <div style={{ fontSize: "13px", color: "#888888", textAlign: "center", marginTop: "8px" }}>
            Ultima modifica in data {formatDate(orderResult.lastUpdate)} alle ore {formatHour(orderResult.lastUpdate)}
          </div>
        </div>
      ) : (
        <OrderSkeleton />
      )}
    </div>
  )
}
